/**
 * Utilidades compartidas para avatares y labels del calendario CRM.
 * Centraliza la lógica de colores, iniciales y etiquetas de tipo
 * para evitar duplicación en CalendarView, CalendarEventCard, etc.
 * @module utils/calendarAvatars
 */

import { format } from 'date-fns';
import type { CalendarEvent } from '@/types/calendar.types';
import { getModalityLabel } from '@/utils/appointmentHelpers';

/** Colores de fondo para avatares, asignados de forma determinista por userId */
export const AVATAR_COLORS = [
  'bg-indigo-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-violet-500',
  'bg-orange-500',
  'bg-cyan-500',
  'bg-pink-500',
  'bg-emerald-500',
  'bg-fuchsia-500',
  'bg-sky-500',
] as const;

/** Obtener iniciales de un nombre (máx 2 caracteres). Tolera null/undefined/vacío → '??'. */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '??';
  const trimmed = name.trim();
  if (!trimmed) return '??';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * Color determinista para un userId (mismo usuario = mismo color siempre).
 * Tolera null/undefined/vacío (p. ej. entradas de log generadas por sistema)
 * devolviendo el primer color de la paleta como fallback estable.
 */
export function getAvatarColor(userId: string | null | undefined): string {
  if (!userId) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Obtener las iniciales a mostrar en el avatar de un evento.
 * Si hay userName, se usan sus iniciales; si no, se usan los 2 primeros chars del userId.
 */
export function getEventAvatarInitials(event: CalendarEvent): string {
  if (event.userName) return getInitials(event.userName);
  if (event.userId) return event.userId.slice(0, 2).toUpperCase();
  return '??';
}

/** Etiqueta corta de tipo de evento para badges */
export function getEventTypeLabel(
  type: string,
  callDirection?: 'inbound' | 'outbound' | null
): string {
  if (type === 'appointment') return 'Cita';
  if (type === 'call') {
    return callDirection === 'inbound' ? 'Entrante' : 'Saliente';
  }
  if (type === 'task') return 'Tarea';
  return type;
}

/**
 * Label optimizado para celdas compactas del grid (semana/mes).
 * Prioriza el nombre del cliente sobre el título genérico.
 *
 * - Citas: nombre del cliente (color azul indica que es cita)
 * - Llamadas: flecha de dirección + nombre del cliente
 * - Tareas: texto de la tarea
 */
export function getGridLabel(event: CalendarEvent): string {
  if (event.type === 'appointment') {
    return event.contactName || 'Sin contacto';
  }
  if (event.type === 'call') {
    const arrow = event.callDirection === 'inbound' ? '↙ ' : '↗ ';
    return event.contactName ? `${arrow}${event.contactName}` : event.title;
  }
  // Tareas: mostrar texto de la tarea (el contacto va en el tooltip)
  return event.title;
}

/**
 * Tooltip informativo multi-línea para hover en celdas del grid.
 * Incluye tipo, hora, cliente, duración, delegación y responsable.
 */
export function getGridTooltip(event: CalendarEvent): string {
  const typeLabel =
    event.type === 'appointment'
      ? 'Cita'
      : event.type === 'call'
        ? event.callDirection === 'inbound'
          ? 'Llamada entrante'
          : 'Llamada saliente'
        : 'Tarea';

  const parts: string[] = [typeLabel];

  try {
    parts.push(format(new Date(event.date), 'HH:mm'));
  } catch {
    /* fecha inválida */
  }

  if (event.contactName) parts.push(`Cliente: ${event.contactName}`);
  else if (event.type === 'appointment') parts.push('Cliente: Sin asignar');
  if (event.type === 'appointment' && event.metadata?.modality) {
    parts.push(`Modalidad: ${getModalityLabel(event.metadata.modality)}`);
  }
  if (event.duration && event.duration > 0) parts.push(`${event.duration} min`);
  if (event.delegationName) parts.push(event.delegationName);
  if (event.userName) parts.push(`Resp: ${event.userName}`);

  return parts.join('\n');
}

/**
 * Nombre principal a mostrar en un evento (prioriza contactName sobre title).
 * Para tareas con contacto, devuelve el contactName.
 * Para tareas sin contacto, devuelve el título de la tarea.
 * Para citas/llamadas, devuelve el contactName o fallback al título.
 */
export function getEventDisplayName(event: CalendarEvent): string {
  return event.contactName || event.title;
}
